import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Close,
  Person,
  Email,
  Phone,
  AttachMoney,
  Description,
  Link,
  Event,
  Error,
  Info,
  CreditCard,
  AccountBalanceWallet,
  Payment,
  Warning,
  Edit,
  Save,
  Cancel,
  VisibilityOff,
  DeleteForever,
} from '@mui/icons-material';
import { PaymentLink, PaymentAttempt, Payment as PaymentType } from '../../types/PaymentLink';
import { formatCurrency, formatDateTime, validateDepositAmount } from '../../utils/validators';
import { getPaymentAttempts, updatePaymentLink } from '../../services/paymentLinkService';
import { getCurrentUser, isSuperAdmin } from '../../services/authService';
import { Timestamp } from 'firebase/firestore';
import StatusChip from '../common/StatusChip';
import CopyLinkButton from '../common/CopyLinkButton';
import { toast } from 'react-toastify';

interface LinkDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentLink: PaymentLink | null;
  onDeleted?: () => void; // Callback when link is soft deleted or permanently deleted
}

const LinkDetailsDialog: React.FC<LinkDetailsDialogProps> = ({ open, onClose, paymentLink, onDeleted }) => {
  const [attempts, setAttempts] = useState<PaymentAttempt[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    description: '',
    invoiceNumber: '',
    invoiceAmount: '',
    depositAmount: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Check if current user is superuser
  const currentUser = getCurrentUser();
  const isSuperUser = isSuperAdmin(currentUser);

  useEffect(() => {
    if (paymentLink && open) {
      loadPaymentAttempts();
      setActiveTab(0);
      setIsEditing(false);
      // Initialize edit form
      setEditForm({
        customerName: paymentLink.customerName || '',
        customerEmail: paymentLink.customerEmail || '',
        customerPhone: paymentLink.customerPhone || '',
        description: paymentLink.description || '',
        invoiceNumber: paymentLink.invoiceNumber || '',
        invoiceAmount: paymentLink.invoiceAmount?.toString() || '',
        depositAmount: paymentLink.depositAmount?.toString() || '',
      });
    }
  }, [paymentLink, open]);

  const loadPaymentAttempts = async () => {
    if (!paymentLink) return;

    try {
      const attemptsList = await getPaymentAttempts(paymentLink.linkId);
      setAttempts(attemptsList);
    } catch (error) {
      console.error('Error loading payment attempts:', error);
    }
  };

  // Get successful payments
  const successfulPayments = paymentLink?.payments?.filter(
    (payment) => payment.status === 'success' && !payment.isDeleted
  ) || [];

  // Get failed payments from attempts
  const failedAttempts = attempts.filter(a => a.status === 'failed');

  // Handle edit mode toggle
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    if (paymentLink) {
      setEditForm({
        customerName: paymentLink.customerName || '',
        customerEmail: paymentLink.customerEmail || '',
        customerPhone: paymentLink.customerPhone || '',
        description: paymentLink.description || '',
        invoiceNumber: paymentLink.invoiceNumber || '',
        invoiceAmount: paymentLink.invoiceAmount?.toString() || '',
        depositAmount: paymentLink.depositAmount?.toString() || '',
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!paymentLink) return;

    // Validation
    if (!editForm.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!editForm.customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    if (!editForm.invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }
    const invoiceAmount = parseFloat(editForm.invoiceAmount);
    if (isNaN(invoiceAmount) || invoiceAmount <= 0) {
      toast.error('Valid invoice amount is required');
      return;
    }

    // Validate deposit amount if provided
    const depositTrimmed = editForm.depositAmount.trim();
    let depositAmount = 0;
    if (depositTrimmed) {
      depositAmount = parseFloat(depositTrimmed);
      if (isNaN(depositAmount) || depositAmount < 0) {
        toast.error('Deposit amount must be a non-negative number');
        return;
      }
      if (depositAmount > invoiceAmount) {
        toast.error('Deposit amount cannot exceed invoice amount');
        return;
      }
      if (depositAmount > 0 && !validateDepositAmount(depositAmount, invoiceAmount)) {
        toast.error('Deposit amount cannot exceed invoice amount');
        return;
      }
    }

    setSaving(true);
    try {
      const currentUser = getCurrentUser();
      
      // Calculate remaining amount: invoiceAmount - totalPaid
      const totalPaid = paymentLink.totalPaid || 0;
      const remainingAmount = invoiceAmount - totalPaid;
      
      await updatePaymentLink(paymentLink.id, {
        customerName: editForm.customerName.trim(),
        customerEmail: editForm.customerEmail.trim(),
        customerPhone: editForm.customerPhone.trim(),
        description: editForm.description.trim(),
        invoiceNumber: editForm.invoiceNumber.trim(),
        invoiceAmount: invoiceAmount,
        depositAmount: depositAmount > 0 ? depositAmount : undefined,
        remainingAmount: remainingAmount,
        editedBy: currentUser?.email || undefined,
        editedAt: Timestamp.now(),
      });

      toast.success('Payment link updated successfully');
      setIsEditing(false);
      onClose(); // Close dialog to refresh parent list
    } catch (error) {
      console.error('Error updating payment link:', error);
      toast.error('Failed to update payment link');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  // Soft delete - hide from normal users (only superuser can see)
  const handleSoftDelete = async () => {
    if (!paymentLink) return;

    // Check if there are any credit card payments (non-manual) before deletion
    const hasCreditCardPayments = paymentLink.payments?.some(
      (payment) => payment.status === 'success' && !payment.isManual
    );

    if (hasCreditCardPayments && !paymentLink.deleted) {
      toast.warning('This payment link has credit card transactions. Please review before deletion.');
      // Still allow deletion but with warning
    }

    // Different messages for superuser vs normal users
    let confirmMessage: string;
    if (isSuperUser && paymentLink.deleted) {
      confirmMessage = 'Restore this payment link? It will be visible to all users again.';
    } else if (isSuperUser && !paymentLink.deleted) {
      confirmMessage = 'Delete this payment link? It will be hidden from normal users but can be restored.';
    } else {
      // Normal user
      confirmMessage = 'Delete this payment link? You can contact admin to restore it if needed.';
    }

    if (!window.confirm(confirmMessage)) return;

    setDeleting(true);
    try {
      const currentUser = getCurrentUser();
      await updatePaymentLink(paymentLink.id, {
        deleted: !paymentLink.deleted, // Toggle deleted state
        deletedBy: !paymentLink.deleted ? (currentUser?.email || undefined) : undefined,
        deletedAt: !paymentLink.deleted ? Timestamp.now() : undefined,
      });

      // Different success messages
      if (isSuperUser && paymentLink.deleted) {
        toast.success('Payment link restored');
      } else {
        toast.success('Payment link deleted');
      }
      
      if (onDeleted) onDeleted();
      onClose();
    } catch (error) {
      console.error('Error toggling delete status:', error);
      toast.error('Failed to update payment link');
    } finally {
      setDeleting(false);
    }
  };

  // Permanent delete - only for superuser on deleted links
  const handlePermanentDelete = async () => {
    if (!paymentLink || !isSuperUser) return;

    const confirmMessage = `PERMANENTLY DELETE this payment link?\n\nLink ID: ${paymentLink.linkId}\nCustomer: ${paymentLink.customerName}\nAmount: ${formatCurrency(paymentLink.invoiceAmount, paymentLink.currency)}\n\nThis action CANNOT be undone!`;

    if (!window.confirm(confirmMessage)) return;

    // Double confirmation for safety
    if (!window.confirm('Are you absolutely sure? This will delete the record from Firestore permanently!')) return;

    setDeleting(true);
    try {
      // Permanent delete from Firestore
      const { deletePaymentLink } = await import('../../services/paymentLinkService');
      await deletePaymentLink(paymentLink.id);

      toast.success('Payment link permanently deleted');
      if (onDeleted) onDeleted();
      onClose();
    } catch (error) {
      console.error('Error permanently deleting:', error);
      toast.error('Failed to delete payment link');
    } finally {
      setDeleting(false);
    }
  };

  // Helper to get payment method display
  const getPaymentMethodDisplay = (payment: PaymentType) => {
    if (payment.isManual) {
      const methodNames: Record<string, string> = {
        cash: 'Cash',
        credit_card: 'Credit Card (Manual)',
        bank_transfer: 'Bank Transfer',
        wire_transfer: 'Wire Transfer',
        belek: 'Belek',
        other: 'Other',
      };
      // A reconciled Ziraat payment is entered by hand but still has real card
      // details from the attempt record — show them instead of hiding the card
      // behind the generic "manual" label.
      const manualDetails = [
        payment.cardLastFour ? `Card: ***${payment.cardLastFour}` : null,
        payment.addedBy ? `Added by: ${payment.addedBy}` : null,
      ].filter(Boolean);
      return {
        icon: <AccountBalanceWallet color="primary" />,
        text: `Manual - ${methodNames[payment.paymentMethod || 'other']}`,
        detail: manualDetails.length > 0 ? manualDetails.join(' · ') : undefined,
      };
    }
    return {
      icon: <CreditCard color="success" />,
      text: `Online Payment`,
      detail: payment.cardLastFour
        ? `Card: ${payment.cardType || 'Card'} ***${payment.cardLastFour}`
        : undefined,
    };
  };

  // Helper to get payment type display
  const getPaymentTypeChip = (paymentType: string) => {
    const colors: Record<string, 'primary' | 'success' | 'info'> = {
      deposit: 'primary',
      partial: 'info',
      full: 'success',
    };
    const labels: Record<string, string> = {
      deposit: 'Deposit',
      partial: 'Partial',
      full: 'Full Payment',
    };
    return (
      <Chip
        label={labels[paymentType] || paymentType}
        size="small"
        color={colors[paymentType] || 'default'}
        variant="outlined"
      />
    );
  };

  if (!paymentLink) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info />
          <Typography variant="h6" component="span">
            Payment Link Details
          </Typography>
        </Box>
        {!isEditing && !paymentLink.isSplitPayment && (
          <Tooltip title="Edit Payment Link">
            <IconButton onClick={handleEditClick} size="small" color="primary">
              <Edit />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Summary Card */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Card sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Link ID
                  </Typography>
                  <Typography variant="h5" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    {paymentLink.linkId}
                  </Typography>
                  <StatusChip status={paymentLink.status} />
                </Box>
                <Box textAlign="right">
                  <Typography variant="overline" color="text.secondary">
                    Paid / Invoice
                  </Typography>
                  <Typography variant="h5">
                    <Typography component="span" variant="h5" color="success.main">
                      {formatCurrency(paymentLink.totalPaid, paymentLink.currency)}
                    </Typography>
                    <Typography component="span" variant="h5" color="text.secondary"> / </Typography>
                    <Typography component="span" variant="h5" color="primary">
                      {formatCurrency(paymentLink.invoiceAmount, paymentLink.currency)}
                    </Typography>
                  </Typography>
                  {paymentLink.remainingAmount > 0 && (
                    <Typography variant="body2" color="warning.main">
                      Remaining: {formatCurrency(paymentLink.remainingAmount, paymentLink.currency)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="fullWidth">
            <Tab label="Details" icon={<Info />} iconPosition="start" />
            <Tab
              label={`Payments (${successfulPayments.length})`}
              icon={<Payment />}
              iconPosition="start"
            />
            {failedAttempts.length > 0 && (
              <Tab
                label={`Failed (${failedAttempts.length})`}
                icon={<Warning color="error" />}
                iconPosition="start"
              />
            )}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 2 }}>
          {/* Details Tab */}
          {activeTab === 0 && (
            <>
              {/* Edit Mode */}
              {isEditing ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Edit Payment Link
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Name"
                      value={editForm.customerName}
                      onChange={handleFormChange('customerName')}
                      required
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Email"
                      type="email"
                      value={editForm.customerEmail}
                      onChange={handleFormChange('customerEmail')}
                      required
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Phone"
                      value={editForm.customerPhone}
                      onChange={handleFormChange('customerPhone')}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice Number"
                      value={editForm.invoiceNumber}
                      onChange={handleFormChange('invoiceNumber')}
                      required
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice Amount"
                      type="number"
                      value={editForm.invoiceAmount}
                      onChange={handleFormChange('invoiceAmount')}
                      required
                      disabled={saving}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Deposit Amount (Optional)"
                      type="number"
                      value={editForm.depositAmount}
                      onChange={handleFormChange('depositAmount')}
                      disabled={saving}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Must be less than or equal to invoice amount"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={editForm.description}
                      onChange={handleFormChange('description')}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveEdit}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  {/* View Mode - Customer Information */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Customer Information
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Person color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Name</Typography>
                          <Typography variant="body1">{paymentLink.customerName}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Email color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                          <Typography variant="body1">{paymentLink.customerEmail}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Phone color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Phone</Typography>
                          <Typography variant="body1">{paymentLink.customerPhone || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* View Mode - Payment Details */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Payment Details
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <AttachMoney color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Invoice Amount</Typography>
                          <Typography variant="body1">{formatCurrency(paymentLink.invoiceAmount, paymentLink.currency)}</Typography>
                        </Box>
                      </Box>
                      {paymentLink.depositAmount > 0 && (
                        <Box display="flex" alignItems="center" gap={2}>
                      <AttachMoney color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Deposit Required</Typography>
                        <Typography variant="body1">
                          {formatCurrency(paymentLink.depositAmount, paymentLink.currency)}
                          {paymentLink.depositPaid && <Chip label="Paid" size="small" color="success" sx={{ ml: 1 }} />}
                        </Typography>
                      </Box>
                    </Box>
                      )}
                      {paymentLink.invoiceNumber && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Description color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                            <Typography variant="body1">{paymentLink.invoiceNumber}</Typography>
                          </Box>
                        </Box>
                      )}
                      <Box display="flex" alignItems="start" gap={2}>
                        <Description color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Description</Typography>
                          <Typography variant="body1">{paymentLink.description || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Link Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Link Information
                    </Typography>
                    <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      {paymentLink.type !== 'guest' && (
                        <Box display="flex" alignItems="start" gap={2}>
                          <Link color="action" />
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">Payment URL</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', mb: 1 }}>
                              {paymentLink.linkUrl}
                            </Typography>
                            <CopyLinkButton linkUrl={paymentLink.linkUrl} size="small" />
                          </Box>
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" gap={2}>
                        <Event color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Created</Typography>
                          <Typography variant="body1">{formatDateTime(paymentLink.createdAt.toDate())}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Event color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Expires</Typography>
                          <Typography variant="body1">{formatDateTime(paymentLink.expiryDate.toDate())}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Info color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Visit Count</Typography>
                          <Typography variant="body1">{paymentLink.visitCount}</Typography>
                        </Box>
                      </Box>
                      {paymentLink.createdBy && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Person color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Created By</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {paymentLink.createdBy}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {paymentLink.editedBy && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Edit color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Last Edited By</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {paymentLink.editedBy}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
                </Grid>
              )}
            </>
          )}

          {/* Payments Tab */}
          {activeTab === 1 && (
            <Box>
              {successfulPayments.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Payment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">No payments recorded yet</Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {successfulPayments.map((payment, index) => {
                    const method = getPaymentMethodDisplay(payment);
                    return (
                      <Card key={payment.id || index} variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                              {method.icon}
                              <Box>
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                  <Typography variant="h6" color="success.main">
                                    {formatCurrency(payment.amount, payment.currency)}
                                  </Typography>
                                  {getPaymentTypeChip(payment.paymentType)}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDateTime(payment.timestamp.toDate())}
                                </Typography>
                              </Box>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="body2" fontWeight={500}>
                                {method.text}
                              </Typography>
                              {method.detail && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {method.detail}
                                </Typography>
                              )}
                              {payment.cardHolderName && !payment.isManual && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Holder: {payment.cardHolderName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {payment.transactionId && (
                            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Transaction ID:
                                </Typography>
                                {payment.isManual ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: 'monospace',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {payment.transactionId}
                                  </Typography>
                                ) : (
                                  <Typography
                                    component="a"
                                    href={`https://sanalpos2.ziraatbank.com.tr/ziraat2/#/orders/d/${payment.transactionId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="caption"
                                    sx={{
                                      fontFamily: 'monospace',
                                      color: 'primary.main',
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {payment.transactionId}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Box>
          )}

          {/* Failed Attempts Tab */}
          {activeTab === 2 && failedAttempts.length > 0 && (
            <Box>
              <Stack spacing={2}>
                {failedAttempts.map((attempt) => (
                  <Card key={attempt.id} variant="outlined" sx={{ borderColor: 'error.light' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Error color="error" />
                        <Typography variant="subtitle1" color="error">
                          Failed Attempt #{attempt.attemptNumber}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                          <Typography variant="body2">{formatDateTime(attempt.timestamp.toDate())}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Amount Attempted</Typography>
                          <Typography variant="body2">{formatCurrency(attempt.amount, paymentLink.currency)}</Typography>
                        </Grid>
                        {attempt.cardLastFour && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Card</Typography>
                            <Typography variant="body2">**** {attempt.cardLastFour} ({attempt.cardType})</Typography>
                          </Grid>
                        )}
                        {attempt.errorMessage && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Error Message</Typography>
                            <Typography variant="body2" color="error">{attempt.errorMessage}</Typography>
                          </Grid>
                        )}
                        {attempt.errorCode && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Error Code</Typography>
                            <Typography variant="body2" fontFamily="monospace">{attempt.errorCode}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {/* Delete/Restore Button */}
          {!isEditing && (
            <>
              {isSuperUser && paymentLink?.deleted ? (
                // Superuser sees "Restore" for deleted links
                <Tooltip title="Restore link (make visible to all users)">
                  <Button
                    onClick={handleSoftDelete}
                    variant="outlined"
                    color="success"
                    startIcon={<VisibilityOff />}
                    disabled={deleting || saving}
                  >
                    Restore
                  </Button>
                </Tooltip>
              ) : (
                // Normal users and superuser see "Delete" for non-deleted links
                <Tooltip title={isSuperUser ? "Delete link (can be restored later)" : "Delete link (admin can restore if needed)"}>
                  <Button
                    onClick={handleSoftDelete}
                    variant="outlined"
                    color="error"
                    startIcon={<VisibilityOff />}
                    disabled={deleting || saving}
                  >
                    Delete
                  </Button>
                </Tooltip>
              )}
            </>
          )}

          {/* Permanent Delete Button - Only superuser on deleted links */}
          {!isEditing && isSuperUser && paymentLink?.deleted && (
            <Tooltip title="PERMANENTLY DELETE from database (cannot be undone)">
              <Button
                onClick={handlePermanentDelete}
                variant="outlined"
                color="error"
                startIcon={<DeleteForever />}
                disabled={deleting || saving}
                sx={{ ml: 1 }}
              >
                Permanent Delete
              </Button>
            </Tooltip>
          )}
        </Box>

        <Button onClick={onClose} variant="contained" startIcon={<Close />} disabled={deleting || saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkDetailsDialog;
